import { CombinedGraphQLErrors } from '@apollo/client/errors';
import {
  GraphQLErrorCode,
  GraphQLInternalErrorCode,
  MCP_OAUTH_CLIENT_CREDENTIALS_REQUIRED_ERROR,
} from '@this+that/constants';
import { GraphQLFormattedError } from 'graphql';

function hasInternalErrorCode(
  error: unknown,
  code: GraphQLInternalErrorCode,
): boolean {
  if (!CombinedGraphQLErrors.is(error)) return false;
  return error.errors.some(
    (e: GraphQLFormattedError) => e.extensions?.internalErrorCode === code,
  );
}

export function getInternalErrorCode(
  error: unknown,
): GraphQLInternalErrorCode | null {
  if (!CombinedGraphQLErrors.is(error)) return null;
  for (const e of error.errors as GraphQLFormattedError[]) {
    const code = e.extensions?.internalErrorCode;
    if (code) return code as GraphQLInternalErrorCode;
  }
  return null;
}

export function getGraphQLErrorCode(error: unknown): GraphQLErrorCode | null {
  if (!CombinedGraphQLErrors.is(error)) return null;
  for (const e of error.errors as GraphQLFormattedError[]) {
    const code = e.extensions?.code;
    if (code) return code as GraphQLErrorCode;
  }
  return null;
}

export const isInvalidLabelForDoBoxGenerationError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.INVALID_LABEL_FOR_DOBOX_GENERATION,
  );

export const isEmptyContentForDoBoxGenerationError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.EMPTY_CONTENT_FOR_DOBOX_GENERATION,
  );

export const isOwnerRemovalForbiddenError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.TASK_GROUP_OWNER_REMOVAL_FORBIDDEN,
  );

export const isInsufficientTeamRoleForTriggerError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.INSUFFICIENT_TEAM_ROLE_FOR_TRIGGER,
  );

export const isUserNotMemberOfTeamError = (err: unknown) =>
  hasInternalErrorCode(err, GraphQLInternalErrorCode.USER_NOT_MEMBER_OF_TEAM);

export const isPersonCreatedTriggerNeverFiresError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.PERSON_CREATED_TRIGGER_NEVER_FIRES,
  );

export const isTeamInstagramIntegrationNotFoundError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.TEAM_INSTAGRAM_INTEGRATION_NOT_FOUND,
  );

export const isKnowledgeSpaceVersionConflictError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.KNOWLEDGE_SPACE_VERSION_CONFLICT,
  );

export const isKnowledgeSpaceForbiddenError = (err: unknown) =>
  hasInternalErrorCode(err, GraphQLInternalErrorCode.KNOWLEDGE_SPACE_FORBIDDEN);

export const isKnowledgeSpaceNotOwnerError = (err: unknown) =>
  hasInternalErrorCode(err, GraphQLInternalErrorCode.KNOWLEDGE_SPACE_NOT_OWNER);

export const isKnowledgeSpaceParticipantAlreadyExistsError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.KNOWLEDGE_SPACE_PARTICIPANT_ALREADY_EXISTS,
  );

export const isKnowledgeSpaceParticipantsNotAllowedOnUserSpaceError = (
  err: unknown,
) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.KNOWLEDGE_SPACE_PARTICIPANTS_NOT_ALLOWED_ON_USER_SPACE,
  );

export const isKnowledgeSpaceInvalidParticipantRoleError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.KNOWLEDGE_SPACE_INVALID_PARTICIPANT_ROLE,
  );

export const isKnowledgeSpaceCannotLeaveTeamSpaceError = (err: unknown) =>
  hasInternalErrorCode(
    err,
    GraphQLInternalErrorCode.KNOWLEDGE_SPACE_CANNOT_LEAVE_TEAM_SPACE,
  );

export function isMcpOauthClientCredentialsRequiredError(
  error: unknown,
): boolean {
  if (!CombinedGraphQLErrors.is(error)) {
    return false;
  }
  return error.errors.some(
    (e: GraphQLFormattedError) =>
      e.extensions?.code === MCP_OAUTH_CLIENT_CREDENTIALS_REQUIRED_ERROR,
  );
}

export function getGraphQLErrorCodes(error: unknown): string[] {
  if (!CombinedGraphQLErrors.is(error)) {
    return [];
  }
  return (error.errors as GraphQLFormattedError[])
    .map((e) => e.extensions?.code)
    .filter((code): code is string => typeof code === 'string');
}

export function hasGraphQLErrorCode(
  error: unknown,
  code: GraphQLErrorCode,
): boolean {
  return getGraphQLErrorCodes(error).includes(code);
}

// The first server message carried by a user-facing code (BAD_USER_INPUT,
// CONFLICT); null for anything else so callers fall back to their own copy.
export function getUserFacingErrorMessage(error: unknown): string | null {
  if (!CombinedGraphQLErrors.is(error)) {
    return null;
  }
  const found = (error.errors as GraphQLFormattedError[]).find(
    (e) =>
      e.extensions?.code === GraphQLErrorCode.BAD_USER_INPUT ||
      e.extensions?.code === GraphQLErrorCode.CONFLICT,
  );
  return found?.message ?? null;
}
